/* eslint-disable @typescript-eslint/naming-convention */
import { isProgressConfig, type IService, type LoadingExtraInfo } from '../../shared';
import { EventGenerator } from '../events';
import type { ServiceManager } from '../service-manager';

export class LoadingService implements IService {
    /** 添加加载图层 ID 集合 */
    private __initLoadingLayerIds: Set<string>;
    /** 添加加载图层定时器 */
    private __initLoadingTimer: NodeJS.Timeout | null;

    constructor(readonly service: ServiceManager) {
        new EventGenerator('loading', this, service);
        this.__initLoadingLayerIds = new Set();
        this.__initLoadingTimer = null;
    }

    onInit() {
        if (this.__initLoadingTimer !== null) {
            clearTimeout(this.__initLoadingTimer);
        }
        this.__initLoadingTimer = null;
        this.__initLoadingLayerIds.clear();
        this.setLoadingMap({});
        this.setProgressMap({});
        const timers = this.__progressTimers;
        Object.values(timers).forEach((timer) => {
            clearInterval(timer);
        });
        this.__progressTimers = {};
    }

    onBindEvent() {
        // 监听图层删除，做删除对象
        this.service.flatLayer.onDelete((layerIds) => {
            for (const layerId of layerIds) {
                if (this.check(layerId)) {
                    this.remove(layerId);
                }
            }
        });
    }

    /** 图层加载 */
    readonly loadingMap: Record<string, LoadingExtraInfo[]> = {};
    declare getLoadingMap: () => Record<string, LoadingExtraInfo[]>;
    declare setLoadingMap: (loadingMap: Record<string, LoadingExtraInfo[]>) => void;
    declare onLoadingMapChange: (fn: (loadingMap: Record<string, LoadingExtraInfo[]>) => void) => number;

    /** 进度映射 */
    readonly progressMap: Record<string, number> = {};
    declare getProgressMap: () => Record<string, number>;
    declare setProgressMap: (progressMap: Record<string, number>) => void;
    declare onProgressMapChange: (fn: (progressMap: Record<string, number>) => void) => number;

    /** 进度定时器映射 */
    private __progressTimers: Record<string, NodeJS.Timeout> = {};

    /**
     * 登记排队或加载中的图层，并在首批登记后延迟展示加载动画
     *
     * 副作用：同步登记加载状态，合并 300ms 内的加载提示更新。
     *
     * @param layerId - 图层 ID
     */
    enter(layerId: string) {
        if (this.loadingMap[layerId]?.includes('add')) return;
        this.__initLoadingLayerIds.add(layerId);
        if (this.__initLoadingTimer !== null) return;
        this.__initLoadingTimer = setTimeout(() => {
            this.__initLoadingTimer = null;
            const layers: Record<string, LoadingExtraInfo[]> = {};
            this.__initLoadingLayerIds.forEach((layerId) => {
                layers[layerId] = [...(this.loadingMap[layerId] || []), 'add'];
            });
            this.setLoadingMap({
                ...this.loadingMap,
                ...layers,
            });
            this.__initLoadingLayerIds.clear();
        }, 300);
    }

    /**
     * 结束图层排队或加载状态，并移除添加动画
     *
     * 副作用：清理图层待展示状态和已经展示的添加动画。
     *
     * @param layerId - 图层 ID
     */
    exit(layerId: string) {
        this.remove(layerId, 'add');
        // if (this.loadingMap[layerId]) {
        //     delete this.loadingMap[layerId];
        //     this.setLoadingMap({
        //         ...this.loadingMap,
        //     });
        // }
    }

    /**
     * 检查图层是否正在 loading
     * @param layerId - 图层 ID
     * @returns 是否正在 loading
     */
    check(layerId: string) {
        return this.__initLoadingLayerIds.has(layerId) || !!this.loadingMap[layerId];
    }

    /**
     * 添加图层 loading
     * @param layerId - 图层 ID
     * @param extraInfo - 用于子图层中需要展示多个加载状态时使用，比如表格图层里面的单元格。支持字符串或进度配置对象
     */
    add(layerId: string, extraInfo?: LoadingExtraInfo) {
        let data: LoadingExtraInfo[] = this.loadingMap[layerId] || [];
        if (extraInfo) {
            // 对于字符串类型，使用原有逻辑去重
            if (typeof extraInfo === 'string') {
                data.push(extraInfo);
                data = Array.from(new Set(data));
            } else {
                // 对于 ProgressConfig，检查是否已存在相同配置
                const exists = data.some((item) => isProgressConfig(item) && item.baseTime === extraInfo.baseTime);
                if (!exists) {
                    data.push(extraInfo);
                }
            }
        }
        this.setLoadingMap({
            ...this.loadingMap,
            [layerId]: data,
        });

        // 如果是进度配置，启动进度更新逻辑
        if (extraInfo && isProgressConfig(extraInfo)) {
            // 清理旧的定时器（如果存在）
            const oldTimer = this.__progressTimers[layerId];
            if (oldTimer) {
                clearInterval(oldTimer);
            }

            // 初始化进度为 0
            this.setProgressMap({
                ...this.progressMap,
                [layerId]: 0,
            });

            // 计算更新间隔：baseTime 是分钟，需要转换为毫秒
            const baseTimeMs = extraInfo.baseTime * 60 * 1000;
            const interval = baseTimeMs / 99;
            let step = 0;

            // 创建定时器
            const timer = setInterval(() => {
                step += 1;
                let progress: number;

                // 统一处理：最大值为 99%
                if (step <= 99) {
                    progress = step;
                } else {
                    progress = 99;
                }

                const currentProgress = this.progressMap[layerId];
                // 只有当进度值变化时才更新
                if (currentProgress !== progress) {
                    this.setProgressMap({
                        ...this.progressMap,
                        [layerId]: progress,
                    });
                }

                // 如果达到最大值，停止定时器
                if (progress >= 99) {
                    clearInterval(timer);
                    const timers = this.__progressTimers;
                    delete timers?.[layerId];
                    this.__progressTimers = { ...timers };
                }
            }, interval);

            // 保存定时器引用
            this.__progressTimers[layerId] = timer;
        }
    }

    /**
     * 移除图层 loading
     * @param layerId - 图层 ID
     * @param extraInfo - 额外信息
     */
    remove(layerId: string, extraInfo?: LoadingExtraInfo) {
        if (extraInfo === undefined || extraInfo === 'add') {
            this.__initLoadingLayerIds.delete(layerId);
            if (!this.__initLoadingLayerIds.size && this.__initLoadingTimer !== null) {
                clearTimeout(this.__initLoadingTimer);
                this.__initLoadingTimer = null;
            }
            // 尚未展示的加载提示无需广播空状态，避免批量同步资源反复更新 UI。
            if (extraInfo === 'add' && !this.loadingMap[layerId]?.includes('add')) return;
        }
        let data: LoadingExtraInfo[] = this.loadingMap[layerId] || [];

        // 如果没有传 extraInfo，则清空该图层下所有的 loading
        if (extraInfo === undefined) {
            // 清理该 layerId 的所有定时器和进度值
            const timer = this.__progressTimers[layerId];
            if (timer) {
                clearInterval(timer);
                const timers = this.__progressTimers;
                delete timers[layerId];
                this.__progressTimers = { ...timers };
            }

            const progressMap = this.progressMap;
            delete progressMap[layerId];
            this.setProgressMap({ ...progressMap });

            delete this.loadingMap[layerId];
            this.setLoadingMap({ ...this.loadingMap });
            return;
        }

        // 如果传了 extraInfo，则只移除匹配的项
        if (typeof extraInfo === 'string') {
            // 对于字符串类型，直接过滤
            data = data.filter((info) => info !== extraInfo);
        } else {
            // 对于 ProgressConfig，比较 baseTime
            data = data.filter((info) => !(isProgressConfig(info) && info.baseTime === extraInfo.baseTime));
        }

        // 如果移除了进度配置，清理对应的定时器和进度值
        if (isProgressConfig(extraInfo)) {
            const timer = this.__progressTimers[layerId];
            if (timer) {
                clearInterval(timer);
                const timers = this.__progressTimers;
                delete timers[layerId];
                this.__progressTimers = { ...timers };
            }

            const progressMap = this.progressMap;
            delete progressMap[layerId];
            this.setProgressMap({ ...progressMap });
        }

        if (data.length === 0) {
            // 清理该 layerId 的所有定时器和进度值
            const timer = this.__progressTimers[layerId];
            if (timer) {
                clearInterval(timer);
                const timers = this.__progressTimers;
                delete timers[layerId];
                this.__progressTimers = { ...timers };
            }

            const progressMap = this.progressMap;
            delete progressMap[layerId];
            this.setProgressMap({ ...progressMap });

            delete this.loadingMap[layerId];
            this.setLoadingMap({ ...this.loadingMap });
        } else {
            // 检查剩余数据中是否还有进度配置
            const hasProgressConfig = data.some((info) => isProgressConfig(info));
            if (!hasProgressConfig) {
                // 如果没有进度配置了，清理定时器和进度值
                const timer = this.__progressTimers[layerId];
                if (timer) {
                    clearInterval(timer);
                    const timers = this.__progressTimers;
                    delete timers[layerId];
                    this.__progressTimers = { ...timers };
                }

                const progressMap = this.progressMap;
                delete progressMap[layerId];
                this.setProgressMap({ ...progressMap });
            }
            this.setLoadingMap({ ...this.loadingMap, [layerId]: data });
        }
    }
}
